import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { getLearnerId, onLearnerChange, setLearnerId } from "../lib/learnerId";

export type Learner = {
  id: string; name: string; gender: "boy" | "girl"; year: number | null;
  lessons?: number; quizzes?: number; avgPercent?: number | null; lastActive?: string | null;
};
export const learnerPose = (g: "boy" | "girl") => (g === "girl" ? "girl-happy" : "boy-wave");

/** The signed-in account's children and the one currently learning. `learners` is null until loaded (or when signed out). */
export function useLearners(enabled = true) {
  const [learners, setLearners] = useState<Learner[] | null>(null);
  const [max, setMax] = useState(6);
  const [currentId, setCurrentId] = useState<string | null>(getLearnerId());
  const reload = useCallback(() => {
    if (!enabled) return Promise.resolve();
    return api<{ learners: Learner[]; max: number }>("/api/learners")
      .then((r) => {
        setLearners(r.learners); setMax(r.max);
        const saved = getLearnerId();
        // A stale choice (child removed) falls back to the first child, or to the account itself when there are none.
        if (saved && !r.learners.some((l) => l.id === saved)) setLearnerId(r.learners[0]?.id ?? null);
        else if (!saved && r.learners.length === 1) setLearnerId(r.learners[0]!.id);
      })
      .catch(() => setLearners(null));
  }, [enabled]);
  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => onLearnerChange(() => setCurrentId(getLearnerId())), []);
  const current = learners?.find((l) => l.id === currentId) ?? null;
  return { learners, max, current, select: setLearnerId, reload };
}
